import { useEffect, useState } from "react";
import { DatePickerInput } from "../../../components/form/DatePickerInput";
import { Dropdown, FormField, PhoneInput } from "../../../components/ui/form";
import { useCountryDetection } from "../../../hooks/useCountryDetection";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import * as DateService from "../../../services/dateService";
import * as FormValidationService from "../../../services/formValidationService";
import * as LanguageOptionsService from "../../../services/languageOptionsService";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./PersonalInfoQuestion.module.css";

interface PersonalInfoQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
}

export function PersonalInfoQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: PersonalInfoQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);

	// Auto-detect user's country for phone input
	const { countryCode: detectedCountry } = useCountryDetection();

	// Get validation results
	const validationResult =
		FormValidationService.validatePersonalInfoForm(answers);
	const getFieldError = (fieldName: string) =>
		showValidation
			? FormValidationService.getFieldError(validationResult.errors, fieldName)
			: undefined;

	// Get language options
	const languageOptions = LanguageOptionsService.getLanguageOptions();

	useEffect(() => {
		const date = DateService.getDateFromAnswers(answers);
		setDateOfBirth(date);
	}, [answers.dayOfBirth, answers.monthOfBirth, answers.yearOfBirth, answers]);

	const handleDateChange = (date: Date | null) => {
		setDateOfBirth(date);
		DateService.updateAnswersWithDate(answers, date, onInputChange);

		if (date) {
			logEvent("personal_info_date_selected", undefined, {
				field_name: "dateOfBirth",
				question_type: "personal-info",
				has_date: "true",
			});
		}
	};

	return (
		<div className={styles.container} dir={direction}>
			<div className={styles.fieldsWrapper}>
				<FormField
					label={t("questions.personalInfo.dateOfBirth")}
					htmlFor="dateOfBirth"
					error={!!getFieldError("dateOfBirth")}
				>
					<DatePickerInput
						value={dateOfBirth}
						onChange={handleDateChange}
						placeholder="DD/MM/YYYY"
					/>
				</FormField>

				<FormField
					label={t("questions.personalInfo.language")}
					htmlFor="languagePreference"
					error={!!getFieldError("languagePreference")}
				>
					<Dropdown
						variant="onboarding"
						options={languageOptions}
						value={answers.languagePreference || ""}
						onChange={(value) => {
							onInputChange("languagePreference", value);
							if (value) {
								logEvent("personal_info_language_selected", undefined, {
									field_name: "languagePreference",
									question_type: "personal-info",
									selected_language: value,
								});
							}
						}}
						placeholder={t("questions.personalInfo.selectLanguage")}
						error={!!getFieldError("languagePreference")}
					/>
				</FormField>

				<FormField
					label={t("questions.personalInfo.phone")}
					htmlFor="phoneNumber"
				>
					<PhoneInput
						variant="onboarding"
						defaultCountry={detectedCountry}
						value={answers.phoneNumber || ""}
						onChange={(value) => {
							onInputChange("phoneNumber", value);
							if (value) {
								logEvent("personal_info_phone_entered", undefined, {
									field_name: "phoneNumber",
									question_type: "personal-info",
									has_content: "true",
								});
							}
						}}
						placeholder={t("questions.personalInfo.placeholders.phone")}
					/>
				</FormField>
			</div>
		</div>
	);
}
